import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import useSupabase from '../../../hooks/useSupabase';
import { serviceTypeService } from '../../../services/serviceTypeService';

const NewOrderModal = ({ isOpen, onClose, onCreateOrder }) => {
  const [formData, setFormData] = useState({
    vin: '',
    make: '',
    model: '',
    year: '',
    fuel_type: '',
    transmission: '',
    engine_size: '',
    color: '',
    licensePlate: '',
    dealership: '',
    priority: 'normal',
    description: '',
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    mileage: '',
    serviceType: '',
    totalAmount: '',
    return_date: '',
    return_time: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingVin, setLoadingVin] = useState(false);
  const [selectedServiceItems, setSelectedServiceItems] = useState([]);

  const { orders, vehicles, carHouses, serviceItems, userProfiles, user } = useSupabase();
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [carHouseOptions, setCarHouseOptions] = useState([]);
  const [serviceItemOptions, setServiceItemOptions] = useState([]);
  const [technicianOptions, setTechnicianOptions] = useState([]);

  // Mock car houses data (temporary until Supabase is configured)
  const mockCarHouses = [
    {
      id: '550e8400-e29b-41d4-a716-446655440001',
      name: 'BMW Autohaus München',
      code: 'BMW-MUC',
      address: 'Maximilianstraße 15',
      city: 'München',
      state: 'Bayern',
      status: 'active'
    },
    {
      id: '550e8400-e29b-41d4-a716-446655440002',
      name: 'Audi Zentrum Köln',
      code: 'AUDI-KOL',
      address: 'Aachener Straße 234',
      city: 'Köln',
      state: 'Nordrhein-Westfalen',
      status: 'active'
    },
    {
      id: '550e8400-e29b-41d4-a716-446655440003',
      name: 'Mercedes-Benz Düsseldorf',
      code: 'MB-DUS',
      address: 'Königsallee 92',
      city: 'Düsseldorf',
      state: 'Nordrhein-Westfalen',
      status: 'active'
    },
    {
      id: '550e8400-e29b-41d4-a716-446655440004',
      name: 'Volkswagen Autohaus Essen',
      code: 'VW-ESS',
      address: 'Rüttenscheider Straße 45',
      city: 'Essen',
      state: 'Nordrhein-Westfalen',
      status: 'active'
    }
  ];

  // Mock service items data
  const mockServiceItems = [
    {
      id: 'si-001',
      name: 'Ölwechsel',
      description: 'Vollständiger Ölwechsel mit Filter',
      service_type: 'maintenance',
      category: 'Motor',
      estimated_duration_hours: 0.5,
      base_price: 89.99,
      is_active: true
    },
    {
      id: 'si-002',
      name: 'Bremseninspektion',
      description: 'Vollständige Bremseninspektion und Diagnose',
      service_type: 'inspection',
      category: 'Bremsen',
      estimated_duration_hours: 1.0,
      base_price: 149.99,
      is_active: true
    },
    {
      id: 'si-003',
      name: 'Getriebereparatur',
      description: 'Getriebediagnose und Reparatur',
      service_type: 'repair',
      category: 'Getriebe',
      estimated_duration_hours: 4.0,
      base_price: 899.99,
      is_active: true
    }
  ];

  // Mock technicians data
  const mockTechnicians = [
    {
      id: 'tech-001',
      full_name: 'Hans Müller',
      role: 'technician',
      status: 'active',
      department: 'Mechanik'
    },
    {
      id: 'tech-002',
      full_name: 'Sarah Weber',
      role: 'technician',
      status: 'active',
      department: 'Elektronik'
    },
    {
      id: 'tech-003',
      full_name: 'Michael Schmidt',
      role: 'technician',
      status: 'active',
      department: 'Karosserie'
    }
  ];

  // Service type options will be loaded dynamically
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);

  const dealershipOptions = carHouseOptions.map(house => ({
    value: house.id,
    label: house.name
  }));



  const priorityOptions = [
    { value: 'low', label: 'Niedrig' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'Hoch' },
    { value: 'urgent', label: 'Dringend' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen]);

  // Update price when service types are loaded and a service type is selected
  useEffect(() => {
    if (formData.serviceType && serviceTypeOptions.length > 0 && !formData.totalAmount) {
      const selectedServiceType = serviceTypeOptions.find(st => st.value === formData.serviceType);
      if (selectedServiceType && selectedServiceType.price !== null && selectedServiceType.price !== undefined) {
        const numericPrice = Number(selectedServiceType.price);
        if (!isNaN(numericPrice)) {
          setFormData(prev => ({ ...prev, totalAmount: numericPrice.toString() }));
        }
      }
    }
  }, [serviceTypeOptions, formData.serviceType]);

  // When dealership changes, reload service type options with dealership-specific pricing
  useEffect(() => {
    const loadDealerServiceTypes = async () => {
      try {
        if (!formData.dealership) {
          const { data: options } = await serviceTypeService.getServiceTypeOptions();
          setServiceTypeOptions(options || []);
          return;
        }
        
        console.log('Loading service types for dealership:', formData.dealership);
        const { data: options, error } = await serviceTypeService.getServiceTypeOptions({ dealership_id: formData.dealership });
        
        if (error) {
          console.error('Error loading dealership service types:', error);
          return;
        }
        
        console.log('Loaded dealership service types:', options);
        setServiceTypeOptions(options || []);

        // If a service type is already selected, update the price to match the dealership
        if (formData.serviceType && options) {
          const selected = options.find(st => st.value === formData.serviceType);
          if (selected && selected.price !== undefined && selected.price !== null) {
            const numericPrice = Number(selected.price);
            if (!isNaN(numericPrice)) {
              console.log(`Updating price for ${selected.label} to ${numericPrice}€ (${selected.pricing_type})`);
              setFormData(prev => ({ ...prev, totalAmount: numericPrice.toString() }));
            }
          }
        }
      } catch (e) {
        console.error('Error loading dealership-specific service types:', e);
      }
    };

    loadDealerServiceTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dealership]);

  const loadInitialData = async () => {
    try {
      // Load vehicles
      const { data: vehiclesData } = await vehicles?.getAll();
      setVehicleOptions(vehiclesData || []);

      // Load car houses - try Supabase first, fallback to mock data
      try {
        const carHousesResult = await carHouses?.getAll();
        const { data: carHousesData } = carHousesResult || {};
        
        if (carHousesData && carHousesData.length > 0) {
          setCarHouseOptions(carHousesData);
        } else {
          setCarHouseOptions(mockCarHouses);
        }
      } catch (carHousesError) {
        setCarHouseOptions(mockCarHouses);
      }

      // Load active service items - try Supabase first, fallback to mock data
      try {
        const { data: serviceItemsData } = await serviceItems?.getAll({ is_active: true });
        if (serviceItemsData && serviceItemsData.length > 0) {
          setServiceItemOptions(serviceItemsData);
        } else {
          setServiceItemOptions(mockServiceItems);
        }
      } catch (serviceItemsError) {
        setServiceItemOptions(mockServiceItems);
      }

      // Load service types - prefer dealership-specific if selected
      try {
        const { data: serviceTypesData } = await serviceTypeService.getServiceTypeOptions(
          formData.dealership ? { dealership_id: formData.dealership } : {}
        );
        if (serviceTypesData && serviceTypesData.length > 0) {
          setServiceTypeOptions(serviceTypesData);
        } else {
          setServiceTypeOptions([]);
        }
      } catch (serviceTypesError) {
        console.error('Error loading service types:', serviceTypesError);
        setServiceTypeOptions([]);
      }

      // Load technicians - try Supabase first, fallback to mock data
      try {
        const { data: techniciansData } = await userProfiles?.getAll({ 
          role: 'technician', 
          status: 'active' 
        });
        if (techniciansData && techniciansData.length > 0) {
          setTechnicianOptions(techniciansData);
        } else {
          setTechnicianOptions(mockTechnicians);
        }
      } catch (techniciansError) {
        setTechnicianOptions(mockTechnicians);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      // Fallback to mock data for all entities
      setCarHouseOptions(mockCarHouses);
      setServiceItemOptions(mockServiceItems);
      setServiceTypeOptions([]);
      setTechnicianOptions(mockTechnicians);
    }
  };

    const handleInputChange = (field, value) => {
    console.log(`handleInputChange called: field=${field}, value=${value}`);
    
    // If service type changes, update the price as well
    if (field === 'serviceType' && value) {
      const selectedServiceType = serviceTypeOptions.find(st => st.value === value);
      
      // Update price based on selected service type
      let newPrice = '';
      if (selectedServiceType) {
        // Check if price exists (including 0) - handle all numeric values including 0
        if (selectedServiceType.price !== null && selectedServiceType.price !== undefined) {
          // Convert to number and then to string to handle both number and string inputs
          const numericPrice = Number(selectedServiceType.price);
          if (!isNaN(numericPrice)) {
            newPrice = numericPrice.toString();
            console.log(`Service type ${selectedServiceType.label} selected with price ${numericPrice}€ (${selectedServiceType.pricing_type || 'default'})`);
          }
        }
      }

      setFormData(prev => ({
        ...prev,
        [field]: value,
        totalAmount: newPrice
      }));
      
    } else {
      setFormData(prev => {
        const newData = {
          ...prev,
          [field]: value
        };
        console.log(`Updated formData for field ${field}:`, newData);
        return newData;
      });
    }

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.vin?.trim()) {
      newErrors.vin = 'VIN ist erforderlich';
    } else if (formData?.vin?.length !== 17) {
      newErrors.vin = 'VIN muss 17 Zeichen lang sein';
    }

    if (!formData?.make?.trim()) {
      newErrors.make = 'Marke ist erforderlich';
    }

    if (!formData?.model?.trim()) {
      newErrors.model = 'Modell ist erforderlich';
    }

    if (!formData?.year?.trim()) {
      newErrors.year = 'Baujahr ist erforderlich';
    } else if (isNaN(parseInt(formData.year)) || parseInt(formData.year) < 1900 || parseInt(formData.year) > new Date().getFullYear() + 1) {
      newErrors.year = 'Ungültiges Baujahr';
    }

    // fuel_type, transmission, and licensePlate are now optional

    if (!formData?.dealership) {
      newErrors.dealership = 'Autohaus ist erforderlich';
    }

    if (!formData?.serviceType) {
      newErrors.serviceType = 'Service-Art ist erforderlich';
    }

    if (formData?.customerEmail && !/\S+@\S+\.\S+/?.test(formData?.customerEmail)) {
      newErrors.customerEmail = 'Ungültige E-Mail-Adresse';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleVinLookup = async () => {
    if (!formData?.vin) return;

    setLoadingVin(true);
    try {
      const { data: vehicleData, error } = await vehicles?.getByVin(formData?.vin);
      
      if (error) {
        setErrors(prev => ({ ...prev, vin: 'Vehicle not found' }));
        return;
      }

      if (vehicleData) {
        setFormData(prev => ({
          ...prev,
          vehicle_id: vehicleData?.id,
          customer_name: vehicleData?.owner_name || '',
          customer_phone: vehicleData?.owner_phone || '',
          customer_email: vehicleData?.owner_email || '',
          car_house_id: vehicleData?.car_house_id || ''
        }));
        
        setErrors(prev => ({ ...prev, vin: '' }));
      }
    } catch (error) {
      console.error('Error looking up VIN:', error);
      setErrors(prev => ({ ...prev, vin: 'Error looking up VIN' }));
    } finally {
      setLoadingVin(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    
    if (!validateForm()) return;

    // Check if user is authenticated
    if (!user?.id) {
      setErrors(prev => ({ ...prev, submit: 'User not authenticated' }));
      return;
    }

    // Validate UUID format for car_house_id
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (formData.dealership && !uuidRegex.test(formData.dealership)) {
      setErrors(prev => ({ ...prev, dealership: 'Invalid dealership ID format' }));
      return;
    }

    console.log('Form data before submission:', formData);
    console.log('Make in formData:', formData.make);
    console.log('Model in formData:', formData.model);
    console.log('TotalAmount in formData:', formData.totalAmount);

    setIsSubmitting(true);
    try {
      // Create order with proper database field mapping
      const orderData = {
        // Vehicle data (will be used to create/update vehicle)
        vin: formData.vin,
        make: formData.make,
        model: formData.model,
        year: formData.year,
        fuel_type: formData.fuel_type,
        transmission: formData.transmission,
        engine_size: formData.engine_size,
        color: formData.color,
        licensePlate: formData.licensePlate,
        mileage: formData.mileage,
        car_house_id: formData.dealership,
        
        // Order data
        title: `Auftrag für ${formData.vin}`,
        description: formData.description || 'Keine Beschreibung',
        status: 'pending',
        priority: formData.priority,
        customer_name: formData.customerName || 'Unbekannt',
        customer_phone: formData.customerPhone || '',
        customer_email: formData.customerEmail || '',
        service_type_id: formData.serviceType, // Use correct field name
        total_amount: formData.totalAmount ? parseFloat(formData.totalAmount) : null, // Use correct field name
        return_date: formData.return_date || null,
        return_time: formData.return_time || null
      };


      console.log('Sending orderData to parent:', orderData);
      console.log('Make in orderData:', orderData.make);
      console.log('Model in orderData:', orderData.model);
      console.log('TotalAmount in orderData:', orderData.totalAmount);
      console.log('Service Type ID:', orderData.service_type_id);
      console.log('Car House ID:', orderData.car_house_id);

      // Use the onCreateOrder prop to let the parent handle order creation
      if (onCreateOrder) {
        await onCreateOrder(orderData);
      } else {
        // Fallback to direct creation if no onCreateOrder prop
        const { data: newOrder, error: createError } = await orders?.create(orderData);

        if (createError) {
          throw createError;
        }

        alert('Auftrag erfolgreich erstellt!');
      }
      
      onClose();
    } catch (error) {
      console.error('Error creating order:', error);
      setErrors(prev => ({ ...prev, submit: error?.message || 'Error creating order' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1000 p-4">
      <div className="bg-card rounded-lg shadow-modal w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-text-primary">Neuer Auftrag</h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Vehicle Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Fahrzeuginformationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="VIN"
                type="text"
                placeholder="17-stellige Fahrzeug-Identifikationsnummer"
                value={formData?.vin}
                onChange={(e) => handleInputChange('vin', e?.target?.value?.toUpperCase())}
                error={errors?.vin}
                required
                maxLength={17}
              />
              
              <Input
                label="Marke"
                type="text"
                placeholder="z.B. BMW, Audi, Mercedes"
                value={formData?.make}
                onChange={(e) => handleInputChange('make', e?.target?.value)}
                error={errors?.make}
                required
              />
              
              <Input
                label="Modell"
                type="text"
                placeholder="z.B. X5, A4, C-Klasse"
                value={formData?.model}
                onChange={(e) => handleInputChange('model', e?.target?.value)}
                error={errors?.model}
                required
              />
              
              <Input
                label="Baujahr"
                type="number"
                placeholder="z.B. 2020"
                value={formData?.year}
                onChange={(e) => handleInputChange('year', e?.target?.value)}
                error={errors?.year}
                required
                min="1900"
                max={new Date().getFullYear() + 1}
              />
              
              <Select
                label="Kraftstoff"
                options={[
                  { value: '', label: 'Kraftstoff auswählen' },
                  { value: 'gasoline', label: 'Benzin' },
                  { value: 'diesel', label: 'Diesel' },
                  { value: 'hybrid', label: 'Hybrid' },
                  { value: 'electric', label: 'Elektrisch' },
                  { value: 'cng', label: 'CNG' },
                  { value: 'lpg', label: 'LPG' }
                ]}
                value={formData?.fuel_type}
                onChange={(value) => handleInputChange('fuel_type', value)}
                error={errors?.fuel_type}
              />
              
              <Select
                label="Getriebe"
                options={[
                  { value: '', label: 'Getriebe auswählen' },
                  { value: 'manual', label: 'Schaltgetriebe' },
                  { value: 'automatic', label: 'Automatik' },
                  { value: 'cvt', label: 'CVT' },
                  { value: 'dual_clutch', label: 'Doppelkupplung' }
                ]}
                value={formData?.transmission}
                onChange={(value) => handleInputChange('transmission', value)}
                error={errors?.transmission}
              />
              
              <Input
                label="Motor"
                type="text"
                placeholder="z.B. 2.0L Turbo, V6"
                value={formData?.engine_size}
                onChange={(e) => handleInputChange('engine_size', e?.target?.value)}
              />
              
              <Select
                label="Farbe"
                options={[
                  { value: '', label: 'Farbe auswählen' },
                  { value: 'blau', label: 'Blau' },
                  { value: 'grün', label: 'Grün' },
                  { value: 'schwarz', label: 'Schwarz' },
                  { value: 'weiß', label: 'Weiß' },
                  { value: 'rot', label: 'Rot' },
                  { value: 'gelb', label: 'Gelb' },
                  { value: 'gold', label: 'Gold' },
                  { value: 'grau', label: 'Grau' },
                  { value: 'silber', label: 'Silber' },
                  { value: 'braun', label: 'Braun' },
                  { value: 'dunkelblau', label: 'Dunkelblau' }
                ]}
                value={formData?.color}
                onChange={(value) => handleInputChange('color', value)}
              />
              
              <Input
                label="Kennzeichen"
                type="text"
                placeholder="z.B. K-AB 1234"
                value={formData?.licensePlate}
                onChange={(e) => handleInputChange('licensePlate', e?.target?.value?.toUpperCase())}
                error={errors?.licensePlate}
              />
              
              <Input
                label="Kilometerstand"
                type="number"
                placeholder="Aktueller Kilometerstand"
                value={formData?.mileage}
                onChange={(e) => handleInputChange('mileage', e?.target?.value)}
              />
              
              <Select
                label="Service-Art"
                options={serviceTypeOptions}
                value={formData?.serviceType}
                onChange={(value) => handleInputChange('serviceType', value)}
                error={errors?.serviceType}
                required
              />

              <Input
                label="Gesamtpreis (€)"
                type="number"
                placeholder="0.00"
                value={formData?.totalAmount}
                onChange={(e) => handleInputChange('totalAmount', e?.target?.value)}
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Order Information */}
          <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Auftragsinformationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Autohaus"
                options={dealershipOptions}
                value={formData?.dealership}
                onChange={(value) => handleInputChange('dealership', value)}
                error={errors?.dealership}
                required
                searchable
              />
              
              <Select
                label="Priorität"
                options={priorityOptions}
                value={formData?.priority}
                onChange={(value) => handleInputChange('priority', value)}
              />
              
              <Input
                label="Rückgabedatum"
                type="date"
                value={formData?.return_date}
                onChange={(e) => handleInputChange('return_date', e?.target?.value)}
              />
              
              <Input
                label="Rückgabezeit"
                type="time"
                value={formData?.return_time}
                onChange={(e) => handleInputChange('return_time', e?.target?.value)}
              />
            </div>
            
            {/* <div className="mt-4">
              <Input
                label="Beschreibung"
                type="text"
                placeholder="Detaillierte Beschreibung des Problems oder Services"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                error={errors?.description}
                required
              />
            </div> */}
          </div>

          {/* Customer Information */}
          {/* <div>
            <h3 className="text-lg font-medium text-text-primary mb-4">Kundeninformationen</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Kundenname"
                type="text"
                placeholder="Vor- und Nachname"
                value={formData?.customerName}
                onChange={(e) => handleInputChange('customerName', e?.target?.value)}
                error={errors?.customerName}
                required
              />
              
              <Input
                label="Telefonnummer"
                type="tel"
                placeholder="+49 123 456789"
                value={formData?.customerPhone}
                onChange={(e) => handleInputChange('customerPhone', e?.target?.value)}
              />
              
              <div className="md:col-span-2">
                <Input
                  label="E-Mail-Adresse"
                  type="email"
                  placeholder="kunde@example.com"
                  value={formData?.customerEmail}
                  onChange={(e) => handleInputChange('customerEmail', e?.target?.value)}
                  error={errors?.customerEmail}
                />
              </div>
            </div>
          </div> */}

          {/* Error Display */}
          {errors?.submit && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <span className="text-sm text-destructive font-medium">Fehler beim Erstellen des Auftrags</span>
              </div>
              <p className="text-sm text-destructive mt-1">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Abbrechen
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              iconName="Plus"
              iconPosition="left"
              onClick={() => console.log('Submit button clicked')}
            >
              Auftrag erstellen
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewOrderModal;